import { create } from 'zustand'
import type { NotificationInboxItem } from '../services/notificationEngineService'

function mapInbox(raw: any): NotificationInboxItem | null {
  const id = String(raw.id ?? raw.Id ?? '')
  if (!id) return null
  return {
    id,
    title: String(raw.title ?? raw.Title ?? ''),
    message: String(raw.message ?? raw.Message ?? ''),
    entityName: raw.entityName ?? raw.EntityName,
    entityId: raw.entityId ?? raw.EntityId,
    actionUrl: raw.actionUrl ?? raw.ActionUrl,
    priority: String(raw.priority ?? raw.Priority ?? 'normal'),
    isRead: !!(raw.isRead ?? raw.IsRead),
    isArchived: !!(raw.isArchived ?? raw.IsArchived),
    isHidden: !!(raw.isHidden ?? raw.IsHidden),
    createdAt: String(raw.createdAt ?? raw.CreatedAt ?? new Date().toISOString()),
  }
}

interface InboxState {
  items: NotificationInboxItem[]
  unreadCount: number
  live: NotificationInboxItem[]
  hydrate: (rows: NotificationInboxItem[], unread: number) => void
  incoming: (raw: any) => NotificationInboxItem | null
  dismissLive: (id: string) => void
  markLocalRead: (id: string) => void
  markAllLocalRead: () => void
  hideLocal: (id: string) => void
  unhideLocal: (id: string) => void
  deleteLocal: (id: string) => void
}

export const useInboxNotificationStore = create<InboxState>((set, get) => ({
  items: [],
  unreadCount: 0,
  live: [],
  hydrate: (rows, unread) => set({ items: rows, unreadCount: unread }),
  incoming: (raw) => {
    const row = mapInbox(raw)
    if (!row) return null
    if (get().items.some((x) => x.id === row.id)) return null
    set((s) => ({
      items: [row, ...s.items].slice(0, 80),
      unreadCount: s.unreadCount + (row.isRead ? 0 : 1),
      live: [row, ...s.live].slice(0, 6),
    }))
    return row
  },
  dismissLive: (id) => set((s) => ({ live: s.live.filter((x) => x.id !== id) })),
  markLocalRead: (id) =>
    set((s) => ({
      items: s.items.map((x) => (x.id === id ? { ...x, isRead: true } : x)),
      unreadCount: Math.max(0, s.unreadCount - (s.items.find((x) => x.id === id && !x.isRead) ? 1 : 0)),
      live: s.live.filter((x) => x.id !== id),
    })),
  markAllLocalRead: () =>
    set((s) => ({
      items: s.items.map((x) => ({ ...x, isRead: true })),
      unreadCount: 0,
      live: [],
    })),
  hideLocal: (id) =>
    set((s) => ({
      items: s.items.map((x) => (x.id === id ? { ...x, isHidden: true, isRead: true } : x)),
      unreadCount: Math.max(0, s.unreadCount - (s.items.find((x) => x.id === id && !x.isRead) ? 1 : 0)),
      live: s.live.filter((x) => x.id !== id),
    })),
  unhideLocal: (id) =>
    set((s) => ({
      items: s.items.map((x) => (x.id === id ? { ...x, isHidden: false, isArchived: false } : x)),
    })),
  deleteLocal: (id) =>
    set((s) => ({
      items: s.items.filter((x) => x.id !== id),
      unreadCount: Math.max(0, s.unreadCount - (s.items.find((x) => x.id === id && !x.isRead) ? 1 : 0)),
      live: s.live.filter((x) => x.id !== id),
    })),
}))
